import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Search,
  Upload,
  Download,
  ChevronRight,
  Layers,
  Calendar,
  HardDrive,
  FileText,
  Trees,
  Zap,
  Leaf,
  MapPinned,
  Filter
} from "lucide-react";
import { MAPS, CATEGORIES } from "../data/mock";
import { useToast } from "../hooks/use-toast";

const categoryIcon = {
  Forestry: Trees,
  "Transmission Line": Zap,
  Environmental: Leaf,
  Other: MapPinned
};

const categoryColor = {
  Forestry: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Transmission Line": "bg-amber-50 text-amber-800 border-amber-200",
  Environmental: "bg-sky-50 text-sky-800 border-sky-200",
  Other: "bg-stone-100 text-stone-700 border-stone-200"
};

export default function Dashboard() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [showDownloadedOnly, setShowDownloadedOnly] = useState(false);
  const { toast } = useToast();

  const filtered = useMemo(() => {
    return MAPS.filter((m) => {
      if (activeCat !== "All" && m.category !== activeCat) return false;
      if (showDownloadedOnly && !m.downloaded) return false;
      if (query && !`${m.title} ${m.description} ${m.category}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [query, activeCat, showDownloadedOnly]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <Badge className="bg-emerald-50 text-emerald-800 hover:bg-emerald-50 border-emerald-200 mb-2">My Library</Badge>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-stone-900 tracking-tight">Georeferenced Maps</h1>
          <p className="mt-1.5 text-stone-600">All uploaded and downloaded PDF maps available for your field crews.</p>
        </div>
        <Button
          className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 h-11"
          onClick={() => toast({ title: "Upload PDF map", description: "Drag a GeoPDF into the import window to begin (demo)." })}
        >
          <Upload className="h-4 w-4" /> Import Map
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-6">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search maps by name, area or category…"
            className="pl-10 h-11 border-stone-300 focus-visible:ring-emerald-600"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              className={`px-3.5 h-9 rounded-md text-sm font-medium border transition-colors ${
                activeCat === c
                  ? "bg-emerald-700 border-emerald-700 text-white"
                  : "bg-white border-stone-300 text-stone-700 hover:bg-stone-50"
              }`}
            >
              {c}
            </button>
          ))}
          <button
            onClick={() => setShowDownloadedOnly((v) => !v)}
            className={`px-3.5 h-9 rounded-md text-sm font-medium border transition-colors gap-1.5 inline-flex items-center ${
              showDownloadedOnly
                ? "bg-stone-900 border-stone-900 text-white"
                : "bg-white border-stone-300 text-stone-700 hover:bg-stone-50"
            }`}
          >
            <Filter className="h-3.5 w-3.5" /> Offline only
          </button>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-stone-300 bg-stone-50">
          <FileText className="h-10 w-10 text-stone-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-800">No maps match your filters</h3>
          <p className="text-sm text-stone-600 mt-1">Try clearing the search or selecting a different category.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((m) => {
            const Icon = categoryIcon[m.category] || MapPinned;
            return (
              <Card key={m.id} className="group overflow-hidden border-stone-200 hover:shadow-lg hover:border-emerald-300 transition-all duration-200 bg-white flex flex-col">
                <div className="relative aspect-[16/10] overflow-hidden bg-stone-100">
                  <img
                    src={m.thumbnail}
                    alt={m.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border inline-flex items-center gap-1 ${categoryColor[m.category]}`}>
                      <Icon className="h-3 w-3" /> {m.category}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    {m.downloaded ? (
                      <span className="px-2 py-1 rounded-md bg-white/95 text-[11px] font-medium text-emerald-800 inline-flex items-center gap-1">
                        <Download className="h-3 w-3" /> Offline
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-md bg-white/95 text-[11px] font-medium text-stone-700 inline-flex items-center gap-1">
                        Cloud only
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="text-[15px] font-bold text-stone-900 leading-snug line-clamp-2">{m.title}</h3>
                  <p className="mt-1.5 text-sm text-stone-600 line-clamp-2 leading-relaxed">{m.description}</p>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-[11px] text-stone-600">
                    <div className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-stone-400" /> {m.layers} layers</div>
                    <div className="flex items-center gap-1.5"><HardDrive className="h-3.5 w-3.5 text-stone-400" /> {m.size}</div>
                    <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-stone-400" /> {m.updated}</div>
                  </div>
                  <Link to={`/map/${m.id}`} className="mt-5">
                    <Button className="w-full bg-stone-900 hover:bg-emerald-700 text-white gap-2 group/btn">
                      Open Map <ChevronRight className="h-4 w-4 group-hover/btn:translate-x-0.5 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
